/**
 * "Scan now", and the Apify spend figure.
 *
 * The transport is **not** here. Everything that needs the Worker goes through `invoke` in
 * `./brain`, which is the only place that knows how the browser reaches `/admin/*` at all:
 *
 *   - under `vite dev`, the dev server answers `/brain/<action>` itself
 *     (`apps/dashboard/vite.dev-brain.ts`), attaching `ADMIN_TOKEN` in Node. Same origin, no CORS,
 *     no deploy - which is what makes the button work on a laptop;
 *   - in a build, the Supabase Edge Function does the same job after checking the caller's session
 *     and allowlist row.
 *
 * This file used to hold its own copy of that logic and call `supabase.functions.invoke` directly.
 * That made Scan now and the Spend screen the only two buttons in the dashboard that skipped the
 * dev proxy, so on localhost they failed with "Failed to send a request to the Edge Function"
 * while the prompts screen next door worked - the worst kind of inconsistency, because the
 * dashboard then looks broken in one place and fine in another with no difference to see.
 *
 * What is left here is the vocabulary: what a scan answers with, and the four calls.
 */

import { invoke } from './brain';
import type { BridgeResult } from './brain';
import type { ApifySpend } from './types';

export type { BridgeResult };

export interface ScanStarted {
  scanRunId: string;
  lane: string;
  label: string;
  platform: string;
  actor: string;
  apifyRunId: string | null;
  datasetId: string | null;
  /** `succeeded` when the run finished inside the poll window, else `running`. */
  status: 'succeeded' | 'running';
  /** What the actor returned. **0 is a real answer** and the screen prints it as a warning. */
  itemsRead: number | null;
  costUsd: number | null;
  /** The body that was actually sent, so the input is never a mystery. */
  input: Record<string, unknown>;
}

export interface ScanSkipped {
  lane: string;
  label: string;
  reason: string;
}

export interface ScanResult {
  started: ScanStarted[];
  skipped: ScanSkipped[];
  warnings: string[];
}

export type CollectResult = ScanStarted | ScanSkipped;

/** `collectScan` answers with the run it collected, or the reason it could not. */
export function isSkipped(value: CollectResult): value is ScanSkipped {
  return 'reason' in value;
}

/**
 * Start one actor run per lane. Naming lanes scans only those; omitting them means every lane that
 * is ready, so the definition of "ready" lives in the Worker rather than in the browser.
 */
export function startScan(lanes?: string[]): Promise<BridgeResult<ScanResult>> {
  return invoke<ScanResult>({ action: 'scan', lanes });
}

/** Finish a scan whose run outlived the request. */
export function collectScan(scanRunId: string): Promise<BridgeResult<CollectResult>> {
  return invoke<CollectResult>({ action: 'collect', scanRunId });
}

/** Apify's own monthly usage figure (P2.14). The other half of the ledger to `llm_calls`. */
export function apifySpend(): Promise<BridgeResult<ApifySpend>> {
  return invoke<ApifySpend>({ action: 'apify-spend' });
}
